/**
 * PATH: packages/auth/src/usePermission.ts
 * PURPOSE: React hooks and utilities for RBAC permission checking
 * WHY: Public API surface for the permission system. Re-exports types/constants
 *      from permission-types.ts and the useRBAC hook from rbac-state.ts so that
 *      existing consumers (`import { ... } from './usePermission'`) keep working.
 *
 * USAGE:
 *   import { usePermission, useRBAC, useIsAdmin } from '@quotech/auth'
 *
 *   // Check specific permission
 *   const canEditCFO = usePermission('cfo', 'write')
 *
 *   // Get full permissions
 *   const { permissions, isAdmin, isGlobalAdmin, role } = useRBAC()
 *
 *   // Check admin status
 *   const isAdmin = useIsAdmin()
 *
 * DEPENDENCIES:
 *   - react: useMemo
 *   - ./permission-types: types + constants
 *   - ./rbac-state: useRBAC hook
 * RELATED:
 *   - AUTH-RBAC-TYPES-001 (permission-types.ts)
 *   - AUTH-RBAC-STATE-001 (rbac-state.ts)
 */

import { useMemo } from 'react'

// Re-export everything from extracted modules so the public API doesn't break
export type {
  PermissionAction,
  PermissionScope,
  ModulePermission,
  UserPermissions,
  RBACState,
  Module,
  Permissions,
} from './permission-types'

export { ACTION_LEVELS, MODULES } from './permission-types'
export { useRBAC } from './rbac-state'

// Internal imports for use within this file
import { ACTION_LEVELS, MODULES } from './permission-types'
import type { PermissionAction, UserPermissions, Module } from './permission-types'
import { useRBAC } from './rbac-state'

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook to check a specific permission.
 *
 * @param module - Module code (e.g., 'cfo', 'jobs')
 * @param requiredAction - Minimum required action level
 * @returns boolean - Whether user has permission
 *
 * @example
 * const canEdit = usePermission('cfo', 'write')
 * if (canEdit) { ... }
 */
export function usePermission(
  module: Module | string,
  requiredAction: PermissionAction = 'read'
): boolean {
  const { permissions, isGlobalAdmin } = useRBAC()

  // Global admins always have permission
  if (isGlobalAdmin) return true

  const modulePerm = permissions[module]
  if (!modulePerm) return false

  const userLevel = ACTION_LEVELS[modulePerm.action] ?? 0
  const requiredLevel = ACTION_LEVELS[requiredAction] ?? 0

  return userLevel >= requiredLevel
}

/**
 * Hook to check if current user is an admin (org or global).
 *
 * @returns boolean - Whether user has admin status
 *
 * @example
 * const isAdmin = useIsAdmin()
 * if (isAdmin) { showAdminPanel() }
 */
export function useIsAdmin(): boolean {
  const { isOrgAdmin, isGlobalAdmin } = useRBAC()
  return isOrgAdmin || isGlobalAdmin
}

/**
 * Hook to check if current user is a global admin.
 *
 * @returns boolean - Whether user is global admin
 */
export function useIsGlobalAdmin(): boolean {
  const { isGlobalAdmin } = useRBAC()
  return isGlobalAdmin
}

/**
 * Comprehensive admin check that combines RBAC + legacy Django flags.
 *
 * Use this instead of manually checking `is_superuser`, `is_staff`,
 * `is_admin`, `admin`, and role strings in every hub's App.tsx.
 *
 * @param user - The auth user object (from useAuth().user)
 * @returns boolean - Whether user should be treated as admin
 *
 * @example
 * const { user } = useAuth()
 * const isAdmin = useComputedIsAdmin(user)
 */
// Cached roles we treat as admin while RBAC is still resolving. Mirrors the
// fallback list rbac-state.ts uses when the permissions fetch fails outright,
// so the "loading" and "errored" code paths agree on who is optimistically
// admin. Backend still enforces every privileged action (403), so an
// optimistic grant here only affects whether admin UI is *shown*, never what
// it can *do*.
const OPTIMISTIC_ADMIN_ROLES = [
  'owner', 'admin', 'superadmin', 'org_owner', 'org_admin',
  'organisation admin', 'global_admin',
]

export function useComputedIsAdmin(user: Record<string, any> | null | undefined): boolean {
  const { isGlobalAdmin, isOrgAdmin, loading } = useRBAC()
  const canManageSettings = usePermission('settings', 'write')

  return useMemo(() => {
    if (isGlobalAdmin || isOrgAdmin) return true
    if (canManageSettings) return true
    if (!user) return false

    // Legacy Django flags (backward compat for cached sessions)
    if (user.is_superuser === true) return true
    if (user.is_staff === true) return true

    // RBAC race hardening (BUG-2026-06-26-RBAC-ORG-RACE): while the RBAC
    // fetch is still resolving (e.g. org_id not yet pinned on first paint),
    // do NOT collapse a cached org owner/admin to "not admin" — that is what
    // transiently locks admin-gated UI (Settings → Organisation/Integrations)
    // and makes the Organisation tab appear to "disappear" for the owner.
    // Trust the cached role optimistically until RBAC settles; a genuine
    // member's role is not in this list, so members are never falsely shown
    // admin UI, and the backend enforces every privileged action regardless.
    if (loading) {
      const role = String(user.role || '').toLowerCase()
      if (OPTIMISTIC_ADMIN_ROLES.includes(role)) return true
    }

    return false
  }, [user, isGlobalAdmin, isOrgAdmin, canManageSettings, loading])
}

/**
 * Hook to get list of modules user can access.
 *
 * @param minAction - Minimum action level to consider accessible
 * @returns string[] - List of accessible module codes
 *
 * @example
 * const modules = useAccessibleModules('read')
 * // ['jobs', 'cfo', 'qa', ...]
 */
export function useAccessibleModules(minAction: PermissionAction = 'read'): string[] {
  const { permissions, isGlobalAdmin } = useRBAC()

  // Global admins can access everything
  if (isGlobalAdmin) {
    return [...MODULES]
  }

  const minLevel = ACTION_LEVELS[minAction]

  return Object.entries(permissions)
    .filter(([_, perm]) => {
      const level = ACTION_LEVELS[perm.action] ?? 0
      return level >= minLevel
    })
    .map(([module]) => module)
}

/**
 * Hook to check multiple permissions at once.
 *
 * @param checks - Array of [module, action] tuples to check
 * @returns Record of module -> boolean
 *
 * @example
 * const perms = usePermissions([
 *   ['cfo', 'write'],
 *   ['jobs', 'admin'],
 *   ['qa', 'read'],
 * ])
 * // { cfo: true, jobs: false, qa: true }
 */
export function usePermissions(
  checks: [Module | string, PermissionAction][]
): Record<string, boolean> {
  const { permissions, isGlobalAdmin } = useRBAC()

  return useMemo(() => {
    const result: Record<string, boolean> = {}

    for (const [module, requiredAction] of checks) {
      if (isGlobalAdmin) {
        result[module] = true
        continue
      }

      const modulePerm = permissions[module]
      if (!modulePerm) {
        result[module] = false
        continue
      }

      const userLevel = ACTION_LEVELS[modulePerm.action] ?? 0
      const requiredLevel = ACTION_LEVELS[requiredAction] ?? 0
      result[module] = userLevel >= requiredLevel
    }

    return result
  }, [permissions, isGlobalAdmin, checks])
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Check if user has permission (non-hook version for use outside components)
 *
 * @param permissions - User's permissions object
 * @param module - Module to check
 * @param action - Required action level
 * @returns boolean
 */
export function hasPermission(
  permissions: UserPermissions,
  module: string,
  action: PermissionAction
): boolean {
  const modulePerm = permissions[module]
  if (!modulePerm) return false

  const userLevel = ACTION_LEVELS[modulePerm.action] ?? 0
  const requiredLevel = ACTION_LEVELS[action] ?? 0

  return userLevel >= requiredLevel
}

/**
 * Filter navigation items based on permissions
 *
 * @param items - Array of nav items with module property
 * @param permissions - User's permissions
 * @param isGlobalAdmin - Whether user is global admin
 * @param minAction - Minimum required action
 * @returns Filtered array of nav items
 */
export function filterNavByPermissions<T extends { module?: string }>(
  items: T[],
  permissions: UserPermissions,
  isGlobalAdmin: boolean,
  minAction: PermissionAction = 'read'
): T[] {
  if (isGlobalAdmin) return items

  return items.filter(item => {
    if (!item.module) return true // Items without module are always visible
    return hasPermission(permissions, item.module, minAction)
  })
}
